import { cva as cvaBase } from "class-variance-authority";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export type { VariantProps } from "class-variance-authority";

/** Merge Tailwind classes with conflict resolution (twMerge + clsx). */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * `cva`, with its output routed through `twMerge`.
 *
 * Upstream `cva` only concatenates. It has no merge step in this version (the
 * `onComplete` hook arrives in cva 1.x), so a variant that conflicts with the
 * base survives alongside it:
 *
 *   cva("gap-8", { variants: { tight: { true: "gap-1" } } })({ tight: true })
 *     // upstream -> "gap-8 gap-1"
 *
 * Both classes reach the attribute and the winner is then decided by the order
 * Tailwind emitted them into the stylesheet, not by the order they were
 * written. The author's intent is absent from the output, and no type can
 * describe the mistake: overriding the base is also the *legitimate* reason to
 * declare a variant, so a dead variant and a working one look identical.
 *
 * Merging here makes the last class win deterministically, which is the same
 * rule `cn` already follows. The passthrough `class` / `className` prop is
 * appended by `cva` last, so it still beats both base and variants -- a caller
 * can override the component, which is the behaviour that makes the prop
 * useful.
 *
 * Consumers must import this rather than `class-variance-authority` directly;
 * `noRestrictedImports` in biome.json enforces that, since an import of the raw
 * package silently reintroduces the failure mode above.
 */
export const cva = <T>(
  ...args: Parameters<typeof cvaBase<T>>
): ReturnType<typeof cvaBase<T>> => {
  const variants = cvaBase<T>(...args);
  return (props) => twMerge(variants(props));
};
